import { createContext, useContext, useState, useEffect } from 'react';
import api from "../utils/api"; // ✅ Correct API instance

// ✅ Create Context
const AuthContext = createContext();

// ✅ Provide Context to Entire App
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const refreshProfile = () => {
    setProfileRefreshKey((prev) => prev + 1);
  };
  // ✅ Store organization in localStorage so it survives refresh
  const [organization, setOrganization] = useState(() => {
    // When the app first loads, check localStorage for any saved org
    const saved = localStorage.getItem("organization");
    return saved ? JSON.parse(saved) : null;
  });

  // Anytime organization changes, keep it synced with localStorage
  useEffect(() => {
    if (organization) {
      localStorage.setItem("organization", JSON.stringify(organization));
    } else {
      localStorage.removeItem("organization");
    }
  }, [organization]);


  // ✅ Function to Check Auths from Backend
  const checkAuth = async () => {
    setLoading(true);

    try {
      const response = await api.get('/api/auth/check-auth', { withCredentials: true });
      setIsAuthenticated(response.data.authenticated);
      setUser(response.data.user || null);
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    }
    setLoading(false);
  };


  const login = async (formData) => {
    try {
      const hostname = window.location.hostname.replace(/^www\./, "");
      const isLocalhost = hostname === "localhost";
      const mainDomain = "sportinghip.com";

      // only slug logins need slug (because backend can’t see /:slug/login)
      const slugFromPath = window.location.pathname.split("/")[1] || null;
      const isSlugLogin =
        (hostname === mainDomain || isLocalhost) &&
        slugFromPath &&
        slugFromPath !== "login";

      const payload = {
        ...formData,
        ...(isSlugLogin ? { slug: slugFromPath.toLowerCase() } : {}),
      };

      const response = await api.post("/api/auth/login", payload, {
        withCredentials: true,
      });

      const loggedInUser = response.data.user;

      setUser(loggedInUser);
      setOrganization({
        id: loggedInUser.organization_id,
        name: loggedInUser.slug,
        slug: loggedInUser.slug,
        plan: loggedInUser.plan,
        sports: loggedInUser.sports || [],
      });
      setIsAuthenticated(true);

      return { success: true, user: loggedInUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const googleLogin = async (credential) => {
    try {
      const hostname = window.location.hostname.replace(/^www\./, "");
      const isLocalhost = hostname === "localhost";
      const mainDomain = "sportinghip.com";

      const slugFromPath = window.location.pathname.split("/")[1] || null;
      const isSlugLogin =
        (hostname === mainDomain || isLocalhost) &&
        slugFromPath &&
        slugFromPath !== "login";

      const payload = {
        credential,
        ...(isSlugLogin ? { slug: slugFromPath.toLowerCase() } : {}),
      };

      const response = await api.post("/api/auth/google", payload, {
        withCredentials: true,
      });

      const loggedInUser = response.data.user;

      setUser(loggedInUser);
      setOrganization({
        id: loggedInUser.organization_id,
        name: loggedInUser.slug,
        slug: loggedInUser.slug,
        plan: loggedInUser.plan,
        sports: loggedInUser.sports || [],
      });
      setIsAuthenticated(true);

      return { success: true, user: loggedInUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Google login failed",
      };
    }
  };

  const createUser = async (userData) => {
    try {
      const response = await api.post(
        '/api/auth/register',
        userData,
        { withCredentials: true }
      );

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to create account"
      };
    }
  };


  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "logout") {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Logout Function
  const logout = async () => {
    try {
      const response = await api.post('/api/auth/logout', {}, { withCredentials: true });

      setIsAuthenticated(false);
      setUser(null);

      // 🔥 notify other tabs
      localStorage.setItem("logout", Date.now());

      return response.data;
    } catch (error) {
      console.error('Logout failed:', error);
      return null;
    }
  };



  const deleteUser = async (userId) => {
    console.log(userId)
    try {
      await api.delete(`/api/users/${userId}`, { withCredentials: true });
      return { success: true };
    } catch (error) {
      console.error("Delete failed", error);
      return { success: false, message: 'Failed to delete user' };
    }
  };



  // ✅ Check Auth on First Render
  useEffect(() => {
    const pathname = window.location.pathname;

    const shouldCheckAuth =
      pathname.startsWith("/dashboard") ||
      pathname.startsWith("/organizations") ||
      pathname.startsWith("/wall") ||
      pathname.startsWith("/profile");

    if (shouldCheckAuth) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated, user, loading, checkAuth, login, googleLogin, createUser, logout, deleteUser, organization, profileRefreshKey,
      refreshProfile, setOrganization
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Custom Hook to Use AuthContext
export const useAuth = () => useContext(AuthContext);
